import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    TemplateRef,
} from '@angular/core';
import {CheckboxTreeNodes, CheckboxTreePreset, CheckboxTreePresets} from 'app/shared/components/checkbox-tree/checkbox-tree.interface';
import {CheckboxComponent} from '../checkbox/checkbox.component';
import {CheckboxTree} from './checkbox-tree';
import {CheckboxTreeItem} from './checkbox-tree-item';

@Component({
    selector: 'checkbox-tree',
    templateUrl: './checkbox-tree.component.html',
    styleUrls: ['./checkbox-tree.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CheckboxTreeComponent implements OnInit, OnChanges {
    static NO_OPTIONS_PLACEHOLDER = 'No options';
    @Input() labelTemplate: TemplateRef<any>;
    @Input() nodes: CheckboxTreeNodes;
    @Input() selected: Set<any>;
    @Input() filterQuery: string;
    @Input() noResultsPlaceholder: string;
    @Input() presets: CheckboxTreePresets;
    @Input() selectAllPreset: {label: string};
    @Input() expandOnClickToGroup = false;
    @Input() readonly = false;
    @Output() expand = new EventEmitter<CheckboxTreeItem>();
    @Output() selectedChange = new EventEmitter<Set<any>>();
    @Output() hideEmptyGroup = new EventEmitter<boolean>(false);

    tree: CheckboxTree;

    get noOptionsPlaceholder() {
        return this.noResultsPlaceholder || CheckboxTreeComponent.NO_OPTIONS_PLACEHOLDER;
    }

    constructor(public cd: ChangeDetectorRef) {}

    ngOnInit() {
        this.nodes = this.nodes === undefined ? [] : this.nodes;
        this.selected = this.selected === undefined ? new Set() : this.selected;

        this.tree = new CheckboxTree(this.nodes, this.selected);
    }

    ngOnChanges(changes: SimpleChanges) {
        if (this.tree && changes.hasOwnProperty('selected')) {
            this.tree.changeSelected(changes.selected.currentValue);
        }
        if (changes.nodes) {
            this.tree = new CheckboxTree(this.nodes || [], this.selected);
            this.tree.changeSelected(this.selected);
        }
        if (changes.filterQuery) {
            this.hideEmptyGroup.emit(!this.totalCount);
        }
    }

    trackByFn(index, item: CheckboxTreeItem) {
        return item.label;
    }

    onNodeChanges(node: CheckboxTreeItem, event: boolean) {
        this.tree.toggleNode(node, event);
        this.selectedChange.emit(this.selected);
    }

    onCheckBoxClick(node: CheckboxTreeItem, event: MouseEvent) {
        event.stopPropagation();
        if (this.readonly) {
            event.stopImmediatePropagation();
            event.preventDefault();
            return;
        }
        if (this.expandOnClickToGroup && node.hasChildren()) {
            event.stopImmediatePropagation();
            event.preventDefault();
            this.tree.toggleChildren(node);
            this.cd.markForCheck();
            this.expand.emit(node);
            return;
        }
    }

    onNodeClick(node: CheckboxTreeItem, checkbox?: CheckboxComponent) {
        if (node.hasChildren()) {
            this.tree.toggleChildren(node);
            this.expand.emit(node);
        } else {
            checkbox.invokeChangeCallback(!checkbox.internalValue);
        }
    }

    onPresetToggle(preset: CheckboxTreePreset, state: boolean) {
        this.tree.applyPreset(preset, state);
        this.selectedChange.emit(this.selected);
    }

    onToggleAll(state: boolean) {
        this.tree.toggleAll(state);
        this.selectedChange.emit(this.selected);
    }

    isPresetActive(preset: CheckboxTreePreset): boolean {
        let presetValues = [...preset.values.values()];
        return presetValues.every(value => this.selected.has(value));
    }

    isAllSelected(selected): boolean {
        let all = true;
        for (let item of this.tree.allChildren()) {
            all = all && selected.has(item.value);
        }
        return all;
    }

    get totalCount() {
        let items = this.tree.getFilteredItems(this.filterQuery);
        return items.reduce((count, node) => count + this.tree.getChildrenCount(node), items.length);
    }
}
